import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Check, Copy, ListChecks, Rocket, Users } from 'lucide-react';
import { useSurvey } from '../../../assets/js/SurveyContext';
import { useLanguage } from '../../../assets/js/LanguageContext';
import { localeOf } from '../../../assets/js/locale';
import { sendPinEmails } from '../../../utils/pinEmails';
import LimitExceededModal from './LimitExceededModal';
import Modal from '../../common/Modal';
import SectionEyebrow from '../../common/SectionEyebrow';
import TemplateSteps from '../TemplatesPulse/TemplateSteps';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';
import '../../../assets/styles/SurveyAnswer.css';
import { clampRating } from '../../common-questions/TRating';

const LaunchSurvey = ({ onClose }) => {
  const { t, language } = useLanguage();
  const { diagnosticName, diagnosticImage, questions, participantes = [], fechaInicio, fechaFin } = useSurvey();
  const navigate = useNavigate();
  const locale = localeOf(language);

  const [generatedPin, setGeneratedPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);

  const emails = Array.isArray(participantes.emails) ? participantes.emails : [];
  const readyQuestions = questions.filter((question) => question.configured && question.data);

  const buildShareMessage = () => `${t('share_greeting')}

${t('share_link_intro')}

thepul.se

${t('share_code_intro')}

${generatedPin}

${t('share_long_text_hint')}

${t('share_thanks')}`;

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const renderAnswerPreview = (data) => {
    if (data.type === 'closed') {
      const options = (data.options || []).filter(Boolean);
      if (!options.length) {
        return <p className="tp-preview__warn">{t('template_preview_closed_needs_options')}</p>;
      }
      return (
        <div className="tp-answer__options" aria-hidden="true">
          {options.map((option, index) => (
            <span key={index} className="tp-answer__option">
              <span className="tp-answer__option-mark" />
              <span className="tp-answer__option-label">{String(option).trim()}</span>
            </span>
          ))}
        </div>
      );
    }

    if (data.type === 'rating') {
      const { min, max } = clampRating(data.minValue, data.maxValue);
      const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div
          className={`tp-answer__scale ${values.length > 7 ? 'is-long' : ''}`.trim()}
          style={{ '--scale-count': values.length }}
          aria-hidden="true"
        >
          {values.map((value) => (
            <span key={value} className="tp-answer__scale-btn">{value}</span>
          ))}
        </div>
      );
    }

    return <p className="tp-preview__textarea" aria-hidden="true">{t('survey_your_answer')}</p>;
  };

  const handleSaveAndGeneratePin = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);

    const surveyData = {
      nombreEncuesta: diagnosticName,
      imagenEncuesta: diagnosticImage,
      preguntas: readyQuestions.map(({ data }) => {
        const base = { textoPregunta: data.question, tipoPregunta: data.type };
        if (data.type === 'closed') return { ...base, opciones: data.options || [] };
        if (data.type === 'rating') return { ...base, minValue: data.minValue, maxValue: data.maxValue };
        return base;
      }),
      participantes,
      fechaInicio,
      fechaFin,
    };

    const token = localStorage.getItem('token');
    if (!token) {
      setSaveError(t('preview_save_error'));
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/encuestas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(surveyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.detalles) {
          setLimitInfo(errorData.detalles);
          setShowLimitModal(true);
          return;
        }
        throw new Error(errorData.error || errorData.message || response.status);
      }

      const result = await response.json();
      setGeneratedPin(result.pin);
      setShowPin(true);
    } catch (error) {
      console.error('Error al guardar el diagnóstico:', error);
      setSaveError(t('preview_save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildShareMessage())
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => setCopied(false));
  };

  // "Terminar" y cerrar el modal llaman los dos aqui: el ref evita mandar los emails dos veces
  const finishingRef = useRef(false);
  const handleFinish = async () => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    await sendPinEmails({ emails, pin: generatedPin, language });

    setShowPin(false);
    navigate('/dashboard', { state: { pin: generatedPin } });
  };

  return (
    <div className="tp-choose tp-preview">
      <button type="button" className="tp-choose__back" onClick={onClose}>
        <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
        {t('common_back')}
      </button>

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('template_preview_kicker')}</SectionEyebrow>
          <h1 className="tp-choose__title">{diagnosticName || t('template_add_name')}</h1>
          <p className="tp-choose__lead">{t('preview_lead')}</p>
        </div>
      </header>

      <TemplateSteps current={3} />

      <ul className="tp-preview__summary">
        <li>
          <CalendarDays size={15} strokeWidth={2} aria-hidden="true" />
          {formatDate(fechaInicio)} – {formatDate(fechaFin)}
        </li>
        <li>
          <ListChecks size={15} strokeWidth={2} aria-hidden="true" />
          {t('builder_questions_count').replace('{count}', readyQuestions.length)}
        </li>
        <li>
          <Users size={15} strokeWidth={2} aria-hidden="true" />
          {emails.length > 0
            ? t('setup_participants_loaded').replace('{count}', emails.length)
            : t('preview_no_participants')}
        </li>
      </ul>

      <div className="tp-answer tp-preview__stage">
        <ol className="tp-preview__list">
          {readyQuestions.map((question, index) => (
            <li key={question.id || index} className="tp-answer__card tp-preview__card">
              <p className="tp-answer__qnum">{t('sd_question_n').replace('{n}', index + 1)}</p>
              <h2 className="tp-answer__question">
                {(question.data.question || '').trim() || t('builder_no_text')}
              </h2>
              {renderAnswerPreview(question.data)}
            </li>
          ))}
        </ol>
      </div>

      {saveError && <p className="tp-form__alert" role="alert">{saveError}</p>}

      <div className="tp-preview__actions">
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('preview_back_questions')}
        </button>
        <button type="button" className="btn btn--primary" onClick={handleSaveAndGeneratePin} disabled={saving}>
          <Rocket size={16} strokeWidth={2} aria-hidden="true" />
          {saving ? t('preview_saving') : t('template_generate_pin_save')}
        </button>
      </div>

      <Modal
        open={showPin}
        onOpenChange={(open) => { if (!open) handleFinish(); }}
        variant="confirm"
        title={t('preview_pin_title')}
        description={t('preview_pin_text')}
        confirmText={t('common_finish')}
        onConfirm={handleFinish}
        size="md"
      >
        <div className="tp-preview__pin">
          <span className="tp-preview__pin-label">{t('dash_th_pin')}</span>
          <strong className="tp-preview__pin-value">{generatedPin}</strong>
        </div>
        <p className="tp-preview__share-label">{t('template_share_suggested_text')}</p>
        <textarea className="tp-preview__share" readOnly rows={10} value={buildShareMessage()} />
        <button type="button" className="btn btn--outline tp-preview__copy" onClick={handleCopy}>
          {copied
            ? <Check size={15} strokeWidth={2.5} aria-hidden="true" />
            : <Copy size={15} strokeWidth={2} aria-hidden="true" />}
          {copied ? t('common_copied') : t('common_copy_text')}
        </button>
      </Modal>

      <LimitExceededModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        limitInfo={limitInfo}
      />
    </div>
  );
};

export default LaunchSurvey;
